import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Search, Calendar, MessageSquare, Trash2, ArrowRight, Clock,
  Bookmark, BookMarked, MoreVertical, Download, AlertTriangle,
  Loader2, RefreshCw,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { APP_CONFIG } from '../config/appConfig';
import { authFetch } from '../config/authConfig';

const API = APP_CONFIG.BACKEND_URL;

interface ConversationItem {
  id: string;
  titulo: string;
  preview?: string;
  es_favorita: boolean;
  message_count: number;
  estado: string;
  fecha_creacion: string;
  fecha_actualizacion: string;
}

function timeAgo(iso: string) {
  const diff = Date.now() - new Date(iso).getTime();
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return 'Ahora';
  if (minutes < 60) return `Hace ${minutes} min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `Hace ${hours}h`;
  const days = Math.floor(hours / 24);
  if (days === 1) return 'Ayer';
  if (days < 7) return `Hace ${days} días`;
  if (days < 30) return `Hace ${Math.floor(days / 7)} sem`;
  return `Hace ${Math.floor(days / 30)} mes`;
}

export default function HistoryPage() {
  const { user, hasPermission } = useAuth();
  const navigate = useNavigate();
  const userId = user?.id || '';
  const can = {
    search: hasPermission('historial.buscar'),
    filterFav: hasPermission('historial.filtrar_favoritas'),
    favorite: hasPermission('historial.favorito'),
    download: hasPermission('historial.descargar'),
    deleteConv: hasPermission('historial.eliminar'),
    open: hasPermission('historial.abrir'),
    refresh: hasPermission('historial.refrescar'),
  };

  const [conversations, setConversations] = useState<ConversationItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<'all' | 'bookmarked'>('all');
  const [menuOpen, setMenuOpen] = useState<string | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  const fetchConversations = useCallback(async () => {
    if (!userId) return;
    try {
      setError(null);
      const res = await authFetch(`${API}/api/v1/conversations?user_id=${userId}`);
      if (!res.ok) throw new Error('Error al cargar conversaciones');
      const data: ConversationItem[] = await res.json();
      setConversations(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error desconocido');
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    fetchConversations();
  }, [fetchConversations]);

  useEffect(() => {
    function handleClickOutside() {
      if (menuOpen) setMenuOpen(null);
    }
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [menuOpen]);

  async function handleToggleFavorite(convId: string) {
    setActionLoading(convId);
    try {
      const res = await authFetch(`${API}/api/v1/conversations/${convId}/favorite`, { method: 'POST' });
      if (!res.ok) throw new Error();
      const data = await res.json();
      setConversations(prev =>
        prev.map(c => c.id === convId ? { ...c, es_favorita: data.es_favorita } : c)
      );
    } catch {
      console.error('Error toggling favorite');
    } finally {
      setActionLoading(null);
    }
  }

  async function handleDelete(convId: string) {
    setActionLoading(convId);
    setDeleteConfirmId(null);
    try {
      const res = await authFetch(`${API}/api/v1/conversations/${convId}`, { method: 'DELETE' });
      if (!res.ok) throw new Error();
      setConversations(prev => prev.filter(c => c.id !== convId));
    } catch {
      console.error('Error deleting conversation');
    } finally {
      setActionLoading(null);
    }
  }

  async function handleDownload(convId: string) {
    setMenuOpen(null);
    setActionLoading(convId);
    try {
      const res = await authFetch(`${API}/api/v1/conversations/${convId}/download`);
      if (!res.ok) throw new Error();
      const text = await res.text();
      const blob = new Blob([text], { type: 'text/plain;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `conversacion_${convId.slice(0, 8)}_${new Date().toISOString().slice(0, 10)}.txt`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      console.error('Error downloading conversation');
    } finally {
      setActionLoading(null);
    }
  }

  function handleOpenConversation(convId: string) {
    navigate('/chat', { state: { conversationId: convId } });
  }

  let filtered = conversations;
  if (filter === 'bookmarked') {
    filtered = filtered.filter(c => c.es_favorita);
  }
  if (search) {
    const q = search.toLowerCase();
    filtered = filtered.filter(c =>
      c.titulo.toLowerCase().includes(q) ||
      (c.preview || '').toLowerCase().includes(q)
    );
  }

  const favoriteCount = conversations.filter(c => c.es_favorita).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin mx-auto mb-3" style={{ color: '#EDAA00' }} />
          <p className="text-sm text-gray-500">Cargando historial...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Historial de Sesiones</h1>
          <p className="text-sm text-gray-500 mt-1">Revisa y retoma tus análisis anteriores con el agente.</p>
        </div>
        {can.refresh && (
          <button
            onClick={() => { setLoading(true); fetchConversations(); }}
            className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
            title="Actualizar"
          >
            <RefreshCw className="w-4 h-4 text-gray-400" />
          </button>
        )}
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
          <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0" />
          <div className="flex-1">
            <p className="text-sm text-red-700">{error}</p>
          </div>
          <button
            onClick={() => { setLoading(true); fetchConversations(); }}
            className="text-sm text-red-600 hover:text-red-800 font-medium"
          >
            Reintentar
          </button>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div className="flex gap-1 bg-gray-100 p-1 rounded-lg">
          <button
            onClick={() => setFilter('all')}
            className={`px-4 py-2 text-xs font-medium rounded-md transition-all ${
              filter === 'all' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Todas ({conversations.length})
          </button>
          {can.filterFav && (
            <button
              onClick={() => setFilter('bookmarked')}
              className={`px-4 py-2 text-xs font-medium rounded-md transition-all flex items-center gap-1.5 ${
                filter === 'bookmarked' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <Bookmark className="w-3 h-3" /> Favoritas ({favoriteCount})
            </button>
          )}
        </div>

        {can.search && (
          <div className="relative w-full sm:w-72">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Buscar en historial..."
              className="w-full pl-9 pr-4 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:border-transparent"
              style={{ '--tw-ring-color': '#EDAA00' } as React.CSSProperties}
            />
          </div>
        )}
      </div>

      {/* Sessions List */}
      {filtered.length === 0 ? (
        <div className="text-center py-16">
          <Clock className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-sm text-gray-500">
            {conversations.length === 0
              ? 'Aún no tienes conversaciones. Inicia una desde el chat.'
              : 'No se encontraron sesiones con ese criterio.'}
          </p>
          {conversations.length === 0 && (
            <button
              onClick={() => navigate('/chat')}
              className="mt-4 px-4 py-2 text-sm font-medium text-white rounded-lg transition-all hover:shadow-md"
              style={{ backgroundColor: '#1B2A4A' }}
            >
              Ir al chat
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map((conv, i) => (
            <motion.div
              key={conv.id}
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: i * 0.03 }}
              className={`bg-white rounded-xl border border-gray-100 p-4 hover:shadow-md transition-all group relative ${
                actionLoading === conv.id ? 'opacity-60 pointer-events-none' : ''
              }`}
              style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}
            >
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: '#FFF3D0' }}>
                  <MessageSquare className="w-5 h-5" style={{ color: '#EDAA00' }} />
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <h3 className="text-sm font-semibold text-gray-900 truncate pr-4">{conv.titulo}</h3>
                      {conv.preview && (
                        <p className="text-xs text-gray-500 mt-0.5 line-clamp-1">{conv.preview}</p>
                      )}
                    </div>

                    <div className="flex items-center gap-1 flex-shrink-0">
                      {can.favorite && (
                        <button
                          onClick={() => handleToggleFavorite(conv.id)}
                          className="p-1.5 rounded-lg hover:bg-gray-50 transition-colors"
                          title={conv.es_favorita ? 'Quitar de favoritos' : 'Agregar a favoritos'}
                        >
                          {conv.es_favorita
                            ? <BookMarked className="w-4 h-4 text-yellow-500" />
                            : <Bookmark className="w-4 h-4 text-gray-300" />}
                        </button>
                      )}
                      {(can.download || can.deleteConv) && (
                        <div className="relative">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setMenuOpen(menuOpen === conv.id ? null : conv.id);
                            }}
                            className="p-1.5 rounded-lg hover:bg-gray-50 transition-colors"
                          >
                            <MoreVertical className="w-4 h-4 text-gray-400" />
                          </button>
                          <AnimatePresence>
                            {menuOpen === conv.id && (
                              <motion.div
                                initial={{ opacity: 0, scale: 0.95 }}
                                animate={{ opacity: 1, scale: 1 }}
                                exit={{ opacity: 0, scale: 0.95 }}
                                transition={{ duration: 0.1 }}
                                className="absolute right-0 top-8 z-10 w-40 bg-white border border-gray-200 rounded-lg shadow-lg py-1"
                                onClick={(e: React.MouseEvent) => e.stopPropagation()}
                              >
                                {can.download && (
                                  <button
                                    className="w-full flex items-center gap-2 px-3 py-2 text-xs text-gray-700 hover:bg-gray-50"
                                    onClick={() => handleDownload(conv.id)}
                                  >
                                    <Download className="w-3.5 h-3.5" /> Descargar
                                  </button>
                                )}
                                {can.deleteConv && (
                                  <button
                                    className="w-full flex items-center gap-2 px-3 py-2 text-xs text-red-600 hover:bg-red-50"
                                    onClick={() => { setMenuOpen(null); setDeleteConfirmId(conv.id); }}
                                  >
                                    <Trash2 className="w-3.5 h-3.5" /> Eliminar
                                  </button>
                                )}
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-4 mt-2.5">
                    <span className="flex items-center gap-1 text-[11px] text-gray-400">
                      <MessageSquare className="w-3 h-3" /> {conv.message_count} mensajes
                    </span>
                    <span className="flex items-center gap-1 text-[11px] text-gray-400">
                      <Calendar className="w-3 h-3" /> {timeAgo(conv.fecha_actualizacion)}
                    </span>
                  </div>
                </div>

                {can.open && (
                  <button
                    onClick={() => handleOpenConversation(conv.id)}
                    className="flex-shrink-0 p-2 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-50 opacity-0 group-hover:opacity-100 transition-all self-center"
                    title="Abrir conversación"
                  >
                    <ArrowRight className="w-4 h-4" />
                  </button>
                )}
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <AnimatePresence>
        {deleteConfirmId && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setDeleteConfirmId(null)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-2xl w-full max-w-sm shadow-2xl"
              onClick={(e: React.MouseEvent) => e.stopPropagation()}
            >
              <div className="p-6 text-center">
                <div className="w-12 h-12 rounded-full bg-red-50 flex items-center justify-center mx-auto mb-4">
                  <AlertTriangle className="w-6 h-6 text-red-500" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Eliminar conversación</h3>
                <p className="text-sm text-gray-500 mb-6">
                  ¿Estás seguro de que deseas eliminar esta conversación? Esta acción no se puede deshacer.
                </p>
                <div className="flex gap-3 justify-center">
                  <button
                    onClick={() => setDeleteConfirmId(null)}
                    className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-50 rounded-lg border border-gray-200"
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={() => handleDelete(deleteConfirmId)}
                    className="px-4 py-2 text-sm text-white bg-red-500 hover:bg-red-600 rounded-lg transition-colors"
                  >
                    Eliminar
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
